import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UserPayroll from "../index";
import UserProfile from "../..";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Card from "../../../../components/ui/Card";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { MonthlyPayrollInput, MonthlyPayrollView } from "../../../../models/payroll";
import { GetUsersMonthlyPayslips } from "../../../../api/Payroll/monthlyPayroll";
import YearMonthPicker from "../../../../components/yearMonthPicker";
import { GetCompanyRates } from "../../../../api/General/currency";
import { CompanyCurrencyRate, Currency } from "../../../../models/general";
import { getCompanyRules } from "../../../../api/General/Company";
import { formatNumber } from "../../../../utils";
import CurrencyDropdown from "../../../../components/currencyDropdown";
import Pagination from "../../../../components/Pagination";
import { RootState } from "../../../../store/redux/store/store";

const PayHistory: React.FC = () => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const role = useSelector((state: RootState) => state.user.role);
  const company = useSelector((state: RootState) => state.user.company);
  const companyCurrencyRateFromStore = useSelector((state: RootState) => state.user.CompanyCurrencyRate);

  const { toast, showToast, dismissToast } = useToast();

  const [selectedPageIndex, setselectedPageIndex] = useState(0);
  const [count, setcount] = useState(10);
  const [offset, setoffset] = useState(0);
  const [payHistory, setPayHistory] = useState<MonthlyPayrollView[]>([]);
  const [loading, setLoading] = useState(false);
  // Keep month as 1-12 for API requests (backend expects 1-based month)
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState((new Date().getMonth() + 1));
  const [from, setFrom] = useState(new Date());
  const [to, setTo] = useState(new Date());
  const [companyCurrencyRates, setCompanyCurrencyRates] = useState<CompanyCurrencyRate[]>([]);
  const [companyBaseCurrency, setCompanyBaseCurrency] = useState<Currency | null>();
  const [currency, setCurrency] = useState<any>(0);

  const fetchData = useCallback(async (currencyId: any) => {
    setLoading(true);
    try {
      const userId = id ?? "";
      const result = await GetUsersMonthlyPayslips({ month, year, userIds: [userId], currencyId } as MonthlyPayrollInput);
      setPayHistory(result.payload);

      const companyRatesResp = await GetCompanyRates();
      const rules = await getCompanyRules(company?.id);
      const baseCurrency = rules.payload.currency;
      const baseCurrRate: CompanyCurrencyRate = {
        id: 0,
        baseCurrency: baseCurrency,
        baseCurrencyId: baseCurrency.id,
        foreignCurrency: baseCurrency,
        foreignCurrencyId: 0,
        company: company,
        companyId: company?.id,
        rate: 0,
        createdOn: new Date(),
        isArchived: false
      };
      const rates: CompanyCurrencyRate[] = [baseCurrRate, ...companyRatesResp.payload];
      setCompanyBaseCurrency(baseCurrency);
      setCompanyCurrencyRates(rates);
    } catch (ex) {
      console.log(ex);
      showToast(t('Failed to load pay history'), 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, month, year, company?.id]);

  useEffect(() => {
    // Single source of truth: refetch when (month/year/id/company) via fetchData changes OR selected currency changes
    fetchData(currency?.foreignCurrencyId || companyCurrencyRateFromStore?.id);
  }, [fetchData, currency?.foreignCurrencyId, companyCurrencyRateFromStore?.id]);

  const onPageChange = (index: number): void => {
    setselectedPageIndex(index - 1);
    setoffset((index - 1) * count);
  };

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
        <UserPayroll>
          <div className="flex flex-col gap-6">
            <div className="flex-between">
                <YearMonthPicker
                  date={from}
                  onChange={(d) => { setFrom(d); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }}
                  fromMonth={new Date(new Date().getFullYear() - 8, 0, 1)}
                  toMonth={new Date(new Date().getFullYear() + 1, 11, 31)}
                  compact
                />
                <CurrencyDropdown
                  value={currency}
                  options={companyCurrencyRates}
                  onOptionChange={(option: any) => {
                    setCurrency(option);
                  }}
                />
            </div>

            <Card className="relative overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Month")}</TableHead>
                      <TableHead>{t("Year")}</TableHead>
                      <TableHead>{t("Gross Pay")}</TableHead>
                      <TableHead>{t("Variable Pay")}</TableHead>
                      <TableHead>{t("Adjustments")}</TableHead>
                      <TableHead>{t("Total Net Pay")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payHistory.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                            <p className="text-sm">{t('No pay history found')}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {payHistory
                      .slice(offset, Math.min(offset + count, payHistory.length))
                      .map((row) => (
                        <TableRow key={`${row.year}-${row.month}`}>
                          <TableCell className="font-semibold">{row.month}</TableCell>
                          <TableCell>{row.year}</TableCell>
                          <TableCell>{formatNumber(row.grossSalary)}</TableCell>
                          <TableCell>{formatNumber(row.variablePay)}</TableCell>
                          <TableCell>{formatNumber(row.adjustments)}</TableCell>
                          <TableCell>{formatNumber(row.totalNetPay)}</TableCell>
                          <TableCell>
                            {row.status ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                {t('PAID')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-border text-muted-foreground">
                                {t('UNPAID')}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              {loading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/55">
                  <span className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              )}
            </Card>

            <Pagination
              currentPage={selectedPageIndex + 1}
              totalPages={Math.ceil(payHistory.length / count) || 1}
              onPageChange={onPageChange}
            />

            <ToastRenderer toast={toast} onDismiss={dismissToast} />
          </div>
        </UserPayroll>
      </UserProfile>
    </div>
  );
};

export default PayHistory;
